
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuit, X, CheckCircle2, DollarSign, Clock, ShieldAlert, Ban, MessageSquare, AlertCircle } from 'lucide-react';
import { Order } from '../types';
import { speak, cancelSpeech } from '../services/voice/speechService';

// Respect reduced motion preferences
const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onIgnore: (reason?: string) => void;
    mode?: 'decision' | 'justification';
    strategyType: 'production_retention' | 'dispatch_hold' | 'boomerang' | 'standard';
    orders: Order[];
    savings: number;
    delayImpact: number;
    description: string;
    isSolarMode?: boolean;
    // v59.1: Propriedades para gerenciar fila de múltiplas sugestões
    queuePosition?: number;
    queueTotal?: number;
    hasNext?: boolean;
    hasPrevious?: boolean;
}

const PRESET_REFUSAL_REASONS = [
    "Rota Inviável (Trânsito)",
    "Motoboy Indisponível",
    "Preferência do Cliente",
    "Risco de Atraso Excessivo",
    "Agrupamento Ineficiente"
];

export const SofiaDecisionModal: React.FC<Props> = ({
    isOpen, onClose, onConfirm, onIgnore, mode = 'decision', strategyType, orders, savings, delayImpact, description, isSolarMode = false,
    // v59.1: Props da fila de sugestões
    queuePosition, queueTotal, hasNext, hasPrevious
}) => {
    const [isRefusing, setIsRefusing] = useState(false);
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [customReason, setCustomReason] = useState<string>('');

    // Strategy Context Helpers
    const getStrategyContext = () => {
        switch (strategyType) {
            case 'production_retention':
                return {
                    title: "Segurar para Agrupamento",
                    intro: "Sugestão de Retenção.",
                    confirmLabel: "Segurar Pedido",
                    ignoreLabel: "Não Aguardar Agrupamento",
                    icon: ShieldAlert,
                    color: isSolarMode ? 'text-blue-600' : 'text-blue-400',
                    bg: isSolarMode ? 'bg-blue-100' : 'bg-blue-500/10'
                };
            case 'boomerang':
                return {
                    title: "Estratégia Bumerangue",
                    intro: "Sugestão de Bumerangue.",
                    confirmLabel: "Aceitar Bumerangue",
                    ignoreLabel: "Recusar Bumerangue",
                    icon: BrainCircuit,
                    color: isSolarMode ? 'text-indigo-600' : 'text-indigo-400',
                    bg: isSolarMode ? 'bg-indigo-100' : 'bg-indigo-500/10'
                };
            case 'dispatch_hold':
                return {
                    title: "Aguardar Agrupamento",
                    intro: "Sugestão de Agrupamento.",
                    confirmLabel: "Aguardar",
                    ignoreLabel: "Despachar Individualmente",
                    icon: Ban,
                    color: isSolarMode ? 'text-amber-600' : 'text-amber-400',
                    bg: isSolarMode ? 'bg-amber-100' : 'bg-amber-500/10'
                };
            default:
                return {
                    title: "Otimização Padrão",
                    intro: "Divergência detectada.",
                    confirmLabel: "Seguir Sugestão",
                    ignoreLabel: "Ignorar e Despachar",
                    icon: BrainCircuit,
                    color: isSolarMode ? 'text-teal-700' : 'text-sal-cyber-teal',
                    bg: isSolarMode ? 'bg-teal-50' : 'bg-sal-cyber-teal/10'
                };
        }
    };

    const ctx = getStrategyContext();
    const showRefusalFlow = isRefusing || mode === 'justification';

    // Voice Announcement on Open
    useEffect(() => {
        if (isOpen) {
            setIsRefusing(mode === 'justification');
            setSelectedReason('');
            setCustomReason('');

            const speechText = mode === 'justification'
                ? 'Por favor, justifique a recusa para fins de auditoria.'
                : `Atenção. ${ctx.intro} ${description}. Economia de ${Math.floor(savings)} reais.`;
            speak(speechText);
        } else {
            cancelSpeech();
        }
    }, [isOpen, mode, ctx.intro, description, savings]);

    const handleConfirm = () => {
        let feedback = "Estratégia Confirmada.";
        let orientation = "Prossiga com a operação.";

        switch (strategyType) {
            case 'boomerang':
                orientation = "Oriente o motoboy a retornar ao hub após as entregas.";
                break;
            case 'production_retention':
            case 'dispatch_hold':
                orientation = "Pedidos mantidos na fila de espera.";
                break;
        }

        speak(`${feedback} ${orientation}`);
        setIsRefusing(false);
        onConfirm();
    };

    const handleStartIgnore = () => {
        speak("Por favor, justifique a recusa para fins de auditoria.");
        setIsRefusing(true);
    };

    const handleConfirmRefusal = () => {
        const finalReason = selectedReason || customReason;
        if (!finalReason && customReason.length < 4) return;

        speak("Estratégia ignorada. Prosseguindo.");
        onIgnore(finalReason);
    };

    const wordCount = customReason.trim() === "" ? 0 : customReason.trim().split(/\s+/).length;
    const isCustomValid = wordCount >= 2;
    const canFinalizeRefusal = selectedReason !== "" || (customReason !== "" && isCustomValid);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && !showRefusalFlow) {
            onClose();
        } else if (e.key === 'Escape' && showRefusalFlow && mode !== 'justification') {
            setIsRefusing(false);
        } else if (e.key === 'Escape' && showRefusalFlow && mode === 'justification') {
            onClose();
        }
    }, [showRefusalFlow, mode, onClose]);

    if (!isOpen) return null;

    return (
        <div role="dialog" aria-modal="true" aria-labelledby="sofia-modal-title" onKeyDown={handleKeyDown}>
            <AnimatePresence>
                <div 
                    className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${isSolarMode ? 'bg-slate-200/80 backdrop-blur-md' : 'bg-sal-gunmetal/90 backdrop-blur-md'}`}
                >
                    <button
                        type="button"
                        aria-label="Fechar modal"
                        className="absolute inset-0"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 20 }}
                        animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
                        exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 20 }}
                        className={`relative z-10 w-full max-w-lg rounded-[2.5rem] shadow-[0_50px_100px_rgba(0,0,0,0.8)] border overflow-hidden ${isSolarMode ? 'bg-white border-slate-200 shadow-xl shadow-slate-300' : 'bg-sal-gunmetal-light border-white/10'}`}
                    >
                        <div className="p-8">
                            <div className="flex justify-between items-start mb-8">
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-2xl ${ctx.bg}`} aria-hidden="true">
                                        <ctx.icon className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h3 id="sofia-modal-title" className={`text-2xl font-black uppercase tracking-tighter ${isSolarMode ? 'text-slate-800' : 'text-white'}`}>{showRefusalFlow ? "Justificativa de Recusa" : ctx.title}</h3>
                                        <div className="flex items-center gap-2">
                                            <p className="text-xs text-slate-500 font-bold uppercase tracking-[0.2em]">Assistente de Decisão Sofia</p>
                                            {/* v59.1: Indicador de progresso da fila */}
                                            {queueTotal && queueTotal > 1 && (
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isSolarMode ? 'bg-slate-200 text-slate-600' : 'bg-white/10 text-slate-400'}`}>
                                                    {queuePosition} de {queueTotal}
                                                </span>
                                            )}
                                        </div>
                                        {/* v59.1: Barra de progresso quando há múltiplas sugestões */}
                                        {queueTotal && queueTotal > 1 && (
                                            <div className="mt-2 w-32 h-1 bg-slate-700/30 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-sal-cyber-teal transition-all duration-300"
                                                    style={{ width: `${((queuePosition || 1) / queueTotal) * 100}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className={`transition-colors ${isSolarMode ? 'text-slate-400 hover:text-slate-800' : 'text-slate-600 hover:text-white'}`}
                                    aria-label="Fechar modal"
                                ><X className="w-6 h-6" /></button>
                            </div>

                            {!showRefusalFlow ? (
                                <>
                                    <div className={`p-6 rounded-3xl border mb-8 ${isSolarMode ? 'bg-slate-50 border-slate-200' : 'bg-black/20 border-white/5'}`}>
                                        <h4 className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest mb-2 opacity-70">Justificativa Logística</h4>
                                        <p className={`text-base leading-relaxed font-medium italic ${isSolarMode ? 'text-slate-600' : 'text-slate-300'}`}>
                                            "{description}"
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-8">
                                        <div className={`p-4 border rounded-2xl ${isSolarMode ? 'bg-emerald-50 border-emerald-100' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
                                            <div className={`flex items-center gap-2 mb-1 ${isSolarMode ? 'text-emerald-700' : 'text-emerald-400'}`}>
                                                <DollarSign className="w-4 h-4" aria-hidden="true" />
                                                <span className="text-xs font-black uppercase tracking-widest">Economia Real</span>
                                            </div>
                                            <div className={`text-3xl font-black tracking-tighter ${isSolarMode ? 'text-emerald-900' : 'text-white'}`}>R$ {savings.toFixed(2)}</div>
                                        </div>
                                        <div className={`p-4 border rounded-2xl ${isSolarMode ? 'bg-amber-50 border-amber-100' : 'bg-amber-500/10 border-amber-500/20'}`}>
                                            <div className={`flex items-center gap-2 mb-1 ${isSolarMode ? 'text-amber-700' : 'text-amber-400'}`}>
                                                <Clock className="w-4 h-4" aria-hidden="true" />
                                                <span className="text-xs font-black uppercase tracking-widest">Impacto no Prazo</span>
                                            </div>
                                            <div className={`text-3xl font-black tracking-tighter ${isSolarMode ? 'text-amber-900' : 'text-white'}`}>+{delayImpact} min</div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <button
                                            type="button"
                                            onClick={handleConfirm}
                                            className="w-full py-5 bg-sal-cyber-teal text-sal-gunmetal font-black rounded-2xl shadow-xl hover:brightness-110 active:scale-[0.98] transition-all uppercase text-sm tracking-[0.2em] flex items-center justify-center gap-2"
                                        >
                                            <CheckCircle2 className="w-5 h-5" aria-hidden="true" /> {ctx.confirmLabel}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleStartIgnore}
                                            className={`w-full py-4 font-black uppercase text-xs tracking-widest transition-all ${isSolarMode ? 'text-slate-400 hover:text-rose-600' : 'text-slate-500 hover:text-rose-400'}`}
                                        >
                                            {ctx.ignoreLabel}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <motion.div 
                                    initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 20 }}
                                    animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }}
                                    className="space-y-6"
                                >
                                    <div className={`p-4 rounded-2xl border flex items-center gap-3 ${isSolarMode ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/10 border-amber-500/20'}`}>
                                        <MessageSquare className={`w-5 h-5 ${isSolarMode ? 'text-amber-600' : 'text-amber-500'}`} aria-hidden="true" />
                                        <p className={`text-xs uppercase font-bold tracking-wider ${isSolarMode ? 'text-amber-800' : 'text-amber-200'}`}>Por que você discorda da Sofia?</p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar">
                                        {PRESET_REFUSAL_REASONS.map(reason => (
                                            <button
                                                type="button"
                                                key={reason}
                                                onClick={() => { setSelectedReason(reason); setCustomReason(''); }}
                                                className={`p-3 rounded-xl border text-xs font-black uppercase tracking-wider text-left transition-all ${selectedReason === reason ? 'bg-sal-cyber-teal border-sal-cyber-teal text-sal-gunmetal' : (isSolarMode ? 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100' : 'bg-black/20 border-white/5 text-slate-400 hover:bg-white/5')}`}
                                            >
                                                {reason}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex justify-between items-end px-1">
                                            <label htmlFor="custom-reason" className={`text-xs font-black uppercase tracking-widest ${isSolarMode ? 'text-slate-400' : 'text-slate-500'}`}>Outro Motivo</label>
                                            <span className={`text-xs font-black uppercase ${isCustomValid ? 'text-emerald-400' : 'text-slate-600'}`}>{wordCount} / 2 palavras</span>
                                        </div>
                                        <textarea
                                            id="custom-reason"
                                            value={customReason}
                                            onChange={(e) => { setCustomReason(e.target.value); setSelectedReason(''); }}
                                            className={`w-full border rounded-2xl p-3 text-base placeholder-slate-600 focus:outline-none transition-all h-20 ${isSolarMode ? 'bg-slate-50 text-slate-800' : 'bg-black/20 text-white'} ${customReason !== '' && !isCustomValid ? 'border-rose-500/50' : (isSolarMode ? 'border-slate-200 focus:border-sal-cyber-teal' : 'border-white/10 focus:border-sal-cyber-teal')}`}
                                            placeholder="Descreva o motivo..."
                                            aria-label="Motivo personalizado para recusa"
                                        />
                                    </div>

                                    <div className={`flex gap-3 pt-4 border-t ${isSolarMode ? 'border-slate-100' : 'border-white/5'}`}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (mode === 'justification') {
                                                    onClose();
                                                    return;
                                                }
                                                setIsRefusing(false);
                                            }}
                                            className={`px-6 py-4 font-black transition-colors uppercase text-xs tracking-widest ${isSolarMode ? 'text-slate-400 hover:text-slate-800' : 'text-slate-500 hover:text-white'}`}
                                        >
                                            {mode === 'justification' ? 'Cancelar' : 'Voltar'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!canFinalizeRefusal}
                                            onClick={handleConfirmRefusal}
                                            className={`flex-1 py-4 font-black rounded-2xl shadow-xl transition-all uppercase text-xs tracking-widest flex items-center justify-center gap-2 ${canFinalizeRefusal ? 'bg-rose-500 text-white hover:brightness-110' : (isSolarMode ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-800 text-slate-600 cursor-not-allowed opacity-50')}`}
                                        >
                                            <AlertCircle className="w-4 h-4" aria-hidden="true" /> Confirmar Recusa
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        <div className={`p-4 border-t text-center ${isSolarMode ? 'bg-slate-50 border-slate-100' : 'bg-black/30 border-white/5'}`}>
                            <p className="text-xs text-slate-600 font-bold uppercase tracking-widest leading-relaxed">
                                {showRefusalFlow ? "Este evento será registrado para análise de conformidade." : "Decisões baseadas nos parâmetros de configuração."}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </AnimatePresence>
        </div>
    );
};
